import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/system";

const LogoLink = styled(Link)({
  display: "inline-block",
  "&:hover": {
    opacity: 0.8,
    transition: "opacity 0.3s ease-in-out",
  },
});

const CustomListItem = styled(ListItem)({
  display: "flex",
  alignItems: "flex-start",
  "&::before": {
    content: '"\\2022"',
    color: "#1569AB",
    fontWeight: "bold",
    display: "inline-block",
    width: "1em",
    marginLeft: "-1em",
    marginTop: "0.2em",
  },
});

const Terms = () => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(to bottom right, #f8fafc, #eef2ff)",
        py: 8,
        px: 2,
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            p: { xs: 4, sm: 6, md: 8 },
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 6,
              position: "relative",
            }}
          >
            <IconButton
              onClick={() =>
                window.history.length > 1 ? navigate(-1) : navigate("/")
              }
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <LogoLink to="/">
              <img
                src="/assets/foxtoxlogo.png"
                alt="Foxtox Logo"
                style={{ height: "60px", width: "auto" }}
              />
            </LogoLink>
          </Box>

          <Typography
            variant="h3"
            component="h1"
            align="center"
            fontWeight="fontWeightExtraBold"
            color="text.primary"
            sx={{ mb: 2 }}
          >
            Terms and Conditions
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Last updated: 22/05/2024
          </Typography>

          <Box
            sx={{
              "& > section": { mb: 6 },
              "& > section:last-of-type": { mb: 0 },
            }}
          >
            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Introduction
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                These Terms of Use (“Terms and Conditions”) mandate the terms on
                which the users (“You” or “Your” or “User”) access and register
                on the website{" "}
                <MuiLink href="http://foxtox.in/" color="primary" underline="hover">
                  http://foxtox.in/
                </MuiLink>{" "}
                and/or mobile application ‘Foxtox’ referred to as, “the
                Platform” owned and operated by Investzen Ventures Private
                Limited (Investzen). Investzen shall also allow its associates,
                group and subsidiary companies, together referred to as
                “Company” or “We” or “Us” to use the Platform as per their
                requirements.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Please read the Terms of Use and Privacy Policy carefully before
                registering on the Platform or accessing any material,
                information through the Platform. The Company retains an
                unconditional right to modify or amend this Terms of Use without
                any requirement to notify You of the same. You can determine
                when this Terms of Use was last modified by referring to the
                “Last Updated” legend above. It shall be Your responsibility to
                check these Terms of Use periodically for changes. Accordingly,
                please continue to review the Terms whenever accessing or using
                the Platform. Your use of the Platform as defined above, after
                the posting of modifications to the Terms will constitute your
                acceptance of the Terms, as modified. Your use of the Platform
                on a continuous basis shall signify Your confirmation to the
                changes and the agreement to be legally bound by the same.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Platform Services
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You acknowledge that the Platform allows You to avail the
                services directly from the Company including their products and
                services and facilitate communication with them for such
                services, and other related information. The Company hereby
                grants You, a limited, non-exclusive, non-transferable, royalty
                free license to use the Platform for the purposes of availing
                the services from the Company, collectively hereinafter referred
                to as Platform Services.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You agree and give Your consent that We may share Your Personal
                Information with our associates and/or statutory/regulatory
                authorities to comply with regulatory requirements or
                restrictions applicable to Us.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Investzen Ventures Private Limited (Investzen)
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                It is hereby clarified that the Company does not provide and/or
                influence/intervene direct securities advisory in any form, and
                it is merely providing a digital marketplace platform, Foxtox to
                its users, to facilitate equity investment / derivatives trading
                advisory services, which in turn is provided by the licensed
                SEBI registered Research Analysts / Investment Advisors (hereon
                collectively referred as “The Advisors”) registered on the
                Platform, as it is. Hence Investzen does not possess any license
                from SEBI as of date.
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                You acknowledge that:
              </Typography>
              <List sx={{ pl: 2 }}>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="any text/video form content shared by the Company and/or Foxtox to its users, other than communication of the trading/investment advice related information published by The Advisors, will only be for educational purposes."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="no content shared by the Company and/or Foxtox to users shall mislead / promise / guarantee any fixed returns from trading/investment activities, and shall be solely for educational purposes."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="Foxtox will only act as the channel to publish the trade/investment advice/recommendations given by The Advisors on the Platform as it is, which shall be the source of truth of actual advice for the users at any given time."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="Foxtox shall timely communicate/notify/alert its users regarding the details of the trade recommendations explicitly opted by the user to be notified about on the Platform, through WhatsApp Direct Messages, in a convenient format/language which is easy to understand for the users, which however shall be Your sole responsibility to verify from the actual trade recommendation/advice details published by the Advisor on the Platform, and that the Company shall not be held responsible for any misinterpretation of the communicated message via WhatsApp."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="trading in Derivative instruments / investments in stocks or any other financial products are subject to market risks (including possible loss of the principal amount invested), and You shall read all investment/scheme-related documents carefully, and/or gather independent financial advice and make Your own assessment before making any trading/investment/disinvestment decision."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="You shall be solely responsible for all the trading/investment orders executed by You on Your Broker Platform after carefully validating the trade/investment advice details as published by the Advisor on the Platform."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary={
                      <>
                        In case of any dissatisfaction with the Platform
                        Services, You shall first file a formal complaint with
                        the customer service of the Company and/or Investzen, as
                        may be applicable, prior to pursuing any other recourse.
                        The complaints can be lodged at{" "}
                        <MuiLink href="mailto:foxtox.care@gmail.com">
                          foxtox.care@gmail.com
                        </MuiLink>{" "}
                        or{" "}
                        <MuiLink href="mailto:foxtox.grievance@gmail.com">
                          foxtox.grievance@gmail.com
                        </MuiLink>{" "}
                        and upon lodging a complaint, You agree to provide
                        complete support to the customer service team with such
                        reasonable information as may be sought by them from You
                        along with necessary documents, emails, screenshots as
                        available with You forming part of the said complaint.
                        The decision of the Company and/or Investzen, as may be
                        applicable, on the complaints shall be final and You
                        agree to be bound by the same.
                      </>
                    }
                  />
                </CustomListItem>
              </List>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                The Advisors
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                It is hereby clarified that The Company has separate agreements
                with each Advisor registered and listed on the Platform, with
                detailed terms of their contract which allows them to provide
                advisory services through the Platform. The Company shall
                intimate its users in advance in case of any changes wrt
                validity of advisor’s contract which affects the concerned
                users.
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                You understand that:
              </Typography>
              <List sx={{ pl: 2 }}>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="The Advisors possess an advisory license from Securities and Exchange Board of India (SEBI) which permits them to provide trade/investment advice to customers under certain limitations"
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="The Advisor shall publish trade recommendations on the Platform whenever they find good trading/investment opportunities based on a certain analysis and their understanding of market conditions, but is not bound in any way to mandatorily publish a fixed/minimum number of trade recommendations or at a fixed/maximum interval period."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="The Advisor is expected to always publish trade/investment recommendation/advice keeping the best interests of its subscribers in mind and with good risk management, which however does not guarantee/promise making any profits and/or fixed returns."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="The Advisor is not liable for any losses incurred by the customer, notional or otherwise while placing trade orders on their respective broker platform."
                  />
                </CustomListItem>
                <CustomListItem disableGutters>
                  <ListItemText
                    primaryTypographyProps={{ lineHeight: "1.6" }}
                    primary="The Advisor has made available a Disclosure Document separately on their profile page within the Platform which can be accessed by users at any point to understand/check further details about the Advisor and the advisory services provided by them."
                  />
                </CustomListItem>
              </List>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Use of the Platform
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Subject to compliance with the Terms of Use, the Company hereby
                grants You a non-exclusive, limited privilege to access and use
                this Platform. You agree to use the Platform only: (a) for
                purposes that are permitted by the Terms of Use; and (b) in
                accordance with any applicable law, regulation or generally
                accepted practices or guidelines.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You agree that You shall not copy, reproduce, sell,
                redistribute, publish, enter into a database, display, perform,
                modify, alter, transmit, license, create derivatives from,
                transfer or in any way exploit any part of any information,
                content, materials, services available from or through the
                Platform, except that You may download the Platform for Your own
                personal, internal use and non-commercial use.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You agree that You will not use the Platform in any manner or
                engage in any activity that may damage, disable or impair or
                adversely affect the use of the Platform or interfere with any
                other users’ use, legal rights, or enjoyment of the Platform.
                Further, You agree not to remove any text, copyright or other
                proprietary notices contained in the content downloaded from the
                Platform.
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                Further, You undertake not to:
              </Typography>
              <List sx={{ pl: 2 }}>
                {[
                  "defame, abuse, harass, threaten or otherwise violate the legal rights of others;",
                  "publish, post, upload, distribute or disseminate any inappropriate, profane, defamatory, infringing, obscene, indecent or unlawful topic, name, material or information;",
                  "copy, republish, post, display, translate, transmit, reproduce or distribute any content on the Platform through any medium without obtaining the necessary authorization from the Company;",
                  "conduct or forward surveys, contests, pyramid schemes or chain letters;",
                  "upload or distribute files that contain software or other material protected by applicable intellectual property laws unless You own or control the rights thereto or have received all necessary consents;",
                  "upload or distribute files that contain viruses, corrupted files, or any other similar software or programs that may damage the operation of the Platform or another’s computer;",
                  "engage in any activity that interferes with or disrupts access to the Platform (or the servers and networks which are connected to the Platform);",
                  "attempt to gain unauthorized access to any portion or feature of the Platform, any other systems or networks connected to the Platform, to any of the Company’s server, or through the Platform, by hacking, password mining or any other illegitimate means;",
                  "probe, scan or test the vulnerability of the Platform or any network connected to the Platform, nor breach the security or authentication measures on the Platform or any network connected to the Platform. You may not reverse look-up, trace or seek to trace any information on any other User, of or visitor to, the Platform, to its source, or exploit the Platform or information made available or offered by or through the Platform, in any way whether or not the purpose is to reveal any information, including but not limited to personal identification information, other than Your own information, as provided on the Platform;",
                  "disrupt or interfere with the security of, or otherwise cause harm to, the Platform, systems resources, accounts, passwords, servers or networks connected to or accessible through the Platform or any affiliated or linked sites;",
                  "collect or store data about other Users in connection with the prohibited conduct and activities;",
                  "use any device or software to interfere or attempt to interfere with the proper working of the Platform or any transaction being conducted on the Platform, or with any other person’s use of the Platform;",
                  "use the Platform or any material or content for any purpose that is unlawful or prohibited by these Terms of Use, or to solicit the performance of any illegal activity or other activity which infringes the rights of the Company or other third parties;",
                  "falsify or delete any author attributions, legal or other proper notices or proprietary designations or labels of the origin or source of software or other material contained in a file that is uploaded;",
                  "violate any applicable laws or regulations for the time being in force within or outside India or anyone’s right to privacy or personality;",
                  "violate the Terms of Use contained herein or elsewhere; and",
                  "reverse engineer, modify, copy, distribute, transmit, display, perform, reproduce, publish, license, create derivative works from, transfer, or sell any information or software obtained from the Platform.",
                ].map((item, index) => (
                  <CustomListItem key={index} disableGutters>
                    <ListItemText
                      primaryTypographyProps={{ lineHeight: "1.6" }}
                      primary={item}
                    />
                  </CustomListItem>
                ))}
              </List>
              <Typography variant="body1" paragraph lineHeight="1.6" sx={{ mt: 2 }}>
                You Shall not use Company’s registration details, and/or any
                certificates and/or any general details thereby inducing public
                at large to conduct business, trading activities with You.
                Additionally, You shall ensure that You shall not undertake any
                selling/advisory activities, or carry out any transactions
                and/or mis-sell the products/services offered on the Platform in
                the secondary market under the name and title of the Company,
                its associates, affiliates group and subsidiary companies.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company has the right to suspend, block or deactivate Your
                Foxtox user account as a risk management measure and/or if
                reasonably believes that any regulatory or statutory
                enquiry/investigation etc might arise due to
                trades/transactions/actions done by You and You will not
                question the same.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Intellectual Property Rights
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Platform and all information, content, materials, products
                including, but not limited to text, content, photographs,
                graphics, texts, video and audio content and computer code
                (“Content”) on the Platform is owned and controlled by the
                Company and the design, structure, selection and feel and
                arrangement of the Content is protected by copyright, patent and
                trademark laws and other various intellectual property rights
                either in the favour of the Company, or third parties from whom
                the appropriate permissions have been taken under applicable
                laws. The trademarks, logos and service marks displayed on the
                Platform (“Marks”) are the property of the Company or other
                respective third parties, as the case may be. You are not
                permitted to use the Marks without the express prior written
                consent of the Company or the third party that owns the Marks.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Further, You understand and accept that all information, except
                Your personal information and other data submitted by You for
                the purposes of transacting on the Platform, through the
                Platform shall be deemed the property of the Company, and/or its
                licensors own all the intellectual property rights and materials
                contained on the Platform, and the Company shall be free to use
                any ideas, concepts, know-how or techniques provided by You on
                the Platform, in any manner whatsoever. On initiating a contact
                or query through the Platform, You agree to be contacted by the
                Company or Investzen or any other entities with whom the Company
                has entered into an arrangement for the provisions of Services
                to You.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Disclaimer of Warranties, Indemnification and Limitation of Liability
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You expressly understand and agree that, to the maximum extent
                permitted by applicable law, the Platform and other Content are
                provided by the Company on an “as is” basis without warranty of
                any kind, express, implied, statutory or otherwise, including
                the implied warranties of title, non-infringement,
                merchantability or fitness for a particular purpose. Without
                limiting the foregoing, the Company makes no warranty that (i)
                the Platform or Platform Services will meet Your requirements or
                Your use of the Platform will be uninterrupted, timely, secure
                or error-free; (ii) the results that may be obtained from the
                use of the Platform will be effective, accurate or reliable;
                (iii) any errors or defects in the Platform will be corrected.
                No advice or information, whether oral or written, obtained by
                You from the Company and/or our group entities or affiliates
                shall create any warranty not expressly stated in the Terms of
                Use.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company will have no liability related to any user Content
                arising under intellectual property rights, libel, privacy,
                publicity, obscenity or other laws. The Company also disclaims
                all liability with respect to the misuse, loss, modification or
                unavailability of any user Content. Further, the Company will
                not be liable for any loss that You may incur as a consequence
                of unauthorized use of Your Account or Account information in
                connection with the Platform or the Platform Services either
                with or without your knowledge.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company has endeavored to ensure that all the information on
                the Platform is correct, but the Company neither warrants nor
                makes any representations regarding the quality, accuracy or
                completeness of any data, information regarding the Platform
                Services or otherwise. The Company shall not be responsible for
                the delay or inability to use the Platform or related
                functionalities, the provision of or failure to provide
                functionalities, or for any information, software,
                functionalities and related graphics obtained through the
                Platform, or otherwise arising out of the use of the Platform,
                whether based on contract, tort, negligence, strict liability or
                otherwise. Further, the Company shall not be held responsible
                for non-availability of the Platform during periodic maintenance
                operations or any unplanned suspension of access to the Platform
                that may occur due to technical reasons or for any reason beyond
                the Company’s control. You understand and agree that any
                material or data downloaded or otherwise obtained through the
                Platform is done entirely at Your own discretion and risk, and
                that You will be solely responsible for any damage to Your
                computer systems or loss of data that results from the download
                of such material or data.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You acknowledge that the software and hardware underlying the
                Platform as well as other internet related software which are
                required for accessing the Platform are the legal property of
                the respective vendors. The permission given by the Company to
                access the Platform will not convey any proprietary or ownership
                rights in the above software/hardware to You.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Please note that not all the Platform Services are available in
                all geographical areas and depending on Your location, You may
                not be eligible to avail certain Platform Services . The Company
                and /or its group entities and affiliates reserve the absolute
                right to determine the availability and eligibility for any of
                the Platform Service offered on the Platform.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company is not responsible for the availability of content
                or other services on third party sites linked from the Platform
                and the Company urges You to read the terms of use of the
                respective third party sites, before accessing or registering
                with any of such third party sites. Further, the Company does
                not make any warranties and expressly disclaims all warranties
                express or implied, including without limitation, those of
                merchantability and fitness for a particular purpose, title or
                non-infringement with respect to any information or services or
                products that are available or advertised or sold through these
                third-party websites.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company shall not be liable for failure or error of any
                transaction on the Platform or for any failure on part of the
                Company to perform any of its obligations under these Terms of
                Use if performance is prevented, hindered or delayed by a Force
                Majeure event (defined below) and in such case its obligations
                under these Terms of Use shall be suspended for so long as the
                Force Majeure event continues. The term “Force Majeure Event”
                means any event due to any cause beyond the reasonable control
                of the affected party, including without limitations,
                unavailability of any communication systems, breach, or virus in
                the processes or payment or delivery mechanism, sabotage, fire,
                flood, explosion, acts of god, civil commotion, strikes or
                industrial action of any kind, riots, insurrection, war, acts of
                government, computer hacking, unauthorised access to computer
                data and storage devices, computer crashes, malfunctioning in
                the computer terminal or the systems getting affected by any
                malicious, destructive or corrupting code or program, mechanical
                or technical errors/failures or power shut down, faults or
                failures in telecommunication etc.
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                Further, You will not dispute or hold the Company responsible
                for:
              </Typography>
              <List sx={{ pl: 2 }}>
                {[
                  "any act that is not an obligation of Company under the Terms of Use;",
                  "any disclosures made by the Company or its group entities/affiliates to any statutory body under any law;",
                  "any loss, notional or otherwise, incurred by You from placing trade orders on Your respective broker platform due to Your interpretation/mis-interpretation of trading/investment recommendations provided by the Advisors available on Foxtox, and/or due to any technical issues faced by You on Your broker platform; and/or due to squaring off your trades/positions by Your Broker as per their respective Risk Policy for factors including but not limited to volatility, margin, outstanding debts, circuit limits (irrespective of margin availability), liquidity etc and you shall be solely liable for the consequences arising thereto.",
                ].map((item, index) => (
                  <CustomListItem key={index} disableGutters>
                    <ListItemText
                      primaryTypographyProps={{ lineHeight: "1.6" }}
                      primary={item}
                    />
                  </CustomListItem>
                ))}
              </List>
              <Typography variant="body1" paragraph lineHeight="1.6" sx={{ mt: 2 }}>
                You agree to indemnify the Company, its directors and employees
                from any losses, damages, penalties, claims, costs and demands
                (including reasonable attorney fees and legal costs) arising out
                of Your breach or non-performance and/or non-observance of the
                duties and obligations, representations, warranties and
                covenants under the Terms of Use or due to your acts or
                omissions. You further agree to hold the Company and/or its
                group entities and affiliates harmless against any claims made
                by any third party due to, or arising out of, or in connection
                with, Your use of the Platform, any misrepresentation with
                respect to the data or information provided by You, Your
                violation of the Terms of Use, or Your violation of any rights
                of another, including any intellectual property rights. In no
                event shall the Company and/ or its group entities and
                affiliates and their officers, partners, consultants, agents and
                employees, be liable to You or any third party for any special,
                incidental, indirect, consequential or punitive damages
                whatsoever, arising out of or in connection with Your use of or
                access to the Platform or Content on the Platform. The
                limitations and exclusions in the Terms of Use apply to the
                maximum extent permitted by applicable laws.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You warrant that all the details and information provided by You
                to the Company and/or our group entities/affiliates while using
                this Platform (including for the purposes of carrying out
                investments) are correct, accurate and genuine. You further
                warrant that providing such details and information on the
                Platform or sharing of the aforesaid details/information shall
                not violate any third – party rights or the intellectual
                property right of any third party.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Eligibility to Use
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                By accepting the Terms of Use, You hereby represent that:
              </Typography>
              <List sx={{ pl: 2 }}>
                {[
                  "You are of 18 (eighteen) years of age or older and in case of You are acting as guardian on behalf of a minor, You have the necessary authority to register/sign up on the Platform for the availing the Services on behalf of the minor;",
                  "You are of legal age to form a binding contract, are not a person barred from receiving the Platform Services under the applicable laws and are competent to enter into a binding contract. The Company reserves the right to refuse access to the Platform to new Users and any such user who has been suspended, disqualified, debarred and/or removed by the Company for any regulatory and/or statutory authority reason whatsoever shall not been entitled to avail the Platform Services; and",
                  "You are prohibited from selling, trading, or otherwise transferring Your Account to another party or impersonating any other person for the purposing of creating an account with the Platform;",
                  "You agree to make an informed independent trading/investment decision, and before investing, You shall seek independent financial planning, legal, accounting, tax or other professional advice, if required.",
                  "You shall be solely responsible for all the trading/investment decisions executed by You on Your Broker Platform, and the Company shall nowhere be responsible for any loss or harm incurred by You due to the trading/investment activities carried out by You on Your broker platform.",
                ].map((item, index) => (
                  <CustomListItem key={index} disableGutters>
                    <ListItemText
                      primaryTypographyProps={{ lineHeight: "1.6" }}
                      primary={item}
                    />
                  </CustomListItem>
                ))}
              </List>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                User Account, Password and Security
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                In order to access the Platform Services on the Platform and
                Your account on the Platform (“Account”), You will have to
                register on the Platform by providing details including but not
                limited to mobile number, email address, password, date of
                birth, gender, etc. and other information as may be required by
                the Company from time to time.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You will be responsible for maintaining the confidentiality of
                the Account information and are fully responsible for all
                activities that occur under Your Account and also agree to keep
                your login credentials safe and confidential at all times. You
                further agree to promptly change your login credentials and
                inform the Company immediately in case of any actual or
                suspected unauthorized use of Your Account. The Company cannot
                and will not be liable for any loss or damage arising from Your
                failure to comply with this provision. You may be held liable
                for losses incurred by the Company or any other user of or
                visitor to the Platform due to authorized or unauthorized use of
                Your Account as a result of Your failure in keeping Your Account
                information secure and confidential.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Violation of the Terms of Use
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You agree that any violation by You of these Terms of Use will
                constititue an unlawful and unfair business practice, and will
                cause irreparable harm to the Company and/ or its group entities
                and affiliates, as the case may be, for which monetary damages
                would be inadequate, and You consent to the Company obtaining
                any injunctive or equitable relief that they deem necessary or
                appropriate in such circumstances. These remedies are in
                addition to any other remedies that the Company may have at law
                or in equity. If the Company takes any legal action against You
                as a result of Your violation of these Terms of Use, they will
                be entitled to recover from You, and You agree to pay, all
                reasonable attorneys’ fees and costs of such action, in addition
                to any other relief that may be granted.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Suspension and Termination
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Terms of Use will continue to apply until terminated by
                either You or the Company as set forth below. If You object to
                the Terms of Use or are dissatisfied with the Platform, your
                only recourse is to (i) close Your Account on the Platform;
                and/or (ii) stop accessing the Platform. In case you violate
                these Terms of Use and/ or any applicable law, the Company may,
                at any time and in its sole discretion, terminate Your Account
                and/or prevent You from accessing the Platform and/or Platform
                Services.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company may delist You or block Your future access to the
                Platform or suspend or terminate your Account if it believes, in
                its sole and absolute discretion that You have infringed,
                breached, violated, abused, or unethically manipulated or
                exploited any term of these Terms of Use or anyway otherwise
                acted unethically. Notwithstanding anything in this clause,
                these Terms of Use will survive indefinitely unless and until
                the Company chooses to terminate them. If You or the Company
                terminate Your use of the Platform, the Company may delete any
                Content or other materials relating to Your and the Company
                shall have no liability to You or any third party for doing so.
                However, Your transactions details may be preserved by the
                Company for purposes of tax or regulatory compliance.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company, in consultation with its group, associates and
                subsidiary companies may modify these terms at any time.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Governing laws
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Terms of Use shall be governed and construed in accordance
                with the laws of India without reference to conflict of laws
                principles. All disputes arising in relation to shall be subject
                to the exclusive jurisdiction of court at Bangalore.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                SEBI Caution
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Investment in the securities market (including mutual fund
                investments) are subject to market risks, please read all
                investments, offer and scheme-related documents carefully,
                before investing.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Report Abuse
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                In the event You come across any abuse or violation of these
                Terms of Use or if You become aware of any objectionable content
                on the Platform, please report the same to the following e-mail
                id:{" "}
                <MuiLink href="mailto:foxtox.care@gmail.com">
                  foxtox.care@gmail.com
                </MuiLink>{" "}
                or{" "}
                <MuiLink href="mailto:foxtox.grievance@gmail.com">
                  foxtox.grievance@gmail.com
                </MuiLink>
                .
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Communications
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You hereby expressly agree to receive communication (including
                transactional messages) or by way of SMS and/or E-mail or through
                WhatsApp from the Company or Investzen or any third party in
                connection with the Platform Services. You can unsubscribe/
                opt-out from receiving communications through SMS and e-mail
                anytime by visiting{" "}
                <MuiLink href="mailto:foxtox.care@gmail.com">
                  foxtox.care@gmail.com
                </MuiLink>
                . However, You understand and agree that all Trading/investment
                recommendations/advice provided by the Advisor subscribed by You
                on the Platform, and/or any related communication shall take
                place through WhatsApp direct messages in the Company’s brand
                name Foxtox, and hence is not an optional channel of
                communication.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Whatsapp Groups
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company reserves a right to create any Whatsapp groups
                (“Groups”) through its representatives (hereinafter referred to
                as “Group Admin”) who are expressly authorized to control,
                monitor and administer the Groups on its behalf. The usage,
                membership, and participation in the Group are subject to the
                following terms and conditions:
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                You shall not:
              </Typography>
              <List sx={{ pl: 2 }}>
                {[
                  "use the Groups for circulating, uploading, transmitting, or otherwise making available any information, data or content that is unlawful, harassing, threatening, harmful, tortuous, defamatory, abusive, violent, obscene, vulgar, invasive of another’s privacy, hateful, racially or ethnically offensive, or otherwise objectionable;",
                  "stalk, harass, threaten or harm another user using the Groups;",
                  "engage in any copyright infringement or other intellectual property infringement, or disclose any trade secret or confidential or personal information in violation of a confidentiality, employment, or nondisclosure agreement and/or privacy norm to which you are subject to;",
                  "post, send, transmit or otherwise make available any unsolicited or unauthorized e-mail messages, advertising, promotional materials, junk mail, spam, or chain letters, including, without limitation, bulk commercial advertising, and informational announcements;",
                  "upload, post, e-mail, transmit, store or otherwise make available any material that contains viruses or any other computer code, files or programs designed to harm, interfere or limit the normal operation of the Service (or any part thereof), or any other computer software or hardware;",
                  "interfere with or disrupt the Group services;",
                  "plan or engage in any illegal activity using the Groups;",
                  "circulate or post any jokes and poems;",
                  "circulate or post any religious or politically oriented views or comments; and",
                  "pretend to be anyone, or any entity, You are not i.e. You may not impersonate or misrepresent yourself as another person (including celebrities), entity, another participant of the Groups, an employee or official or representative of Investzen;",
                ].map((item, index) => (
                  <CustomListItem key={index} disableGutters>
                    <ListItemText
                      primaryTypographyProps={{ lineHeight: "1.6" }}
                      primary={item}
                    />
                  </CustomListItem>
                ))}
              </List>
              <Typography variant="body1" paragraph lineHeight="1.6" sx={{ mt: 2 }}>
                The Group Admin reserves the right at all times to determine,
                in its sole discretion, whether the content posted on the Group
                is appropriate and in compliance with these Terms of Use. The
                Group Admin further reserves the rights to remove any
                participant/member from the Group if it finds You or any
                participant/member to have circulated/posted any content which
                is objectionable or improper for the Groups or its participants
                and/or deemed to have violated the Terms of Use or for any
                reason whatsoever. The participants shall not challenge or
                question such removal by the Group Admin or declaration of any
                content as inappropriate or not compliant with the Terms of Use.
                The participant hereby agrees and acknowledges that any of such
                actions by the Groups Admin shall not be construed as defamatory
                by the participant or You. This Group shall be used only for
                communication and group chat in relation to financial
                investments, and financial products.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The views or comments posted herein by any participant
                (including any recommendation for investment and/or
                disinvestment in any financial products including mutual funds)
                shall be the views and comments of that participant only and
                shall not be construed to be the views or comments of the Group
                Admin and/or the Company, or its group entities/affiliates or
                that such view/comments are endorsed by them.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The participant agrees and acknowledges that
                views/comments/suggestions by any other participants in the
                Groups shall be subject to verification by the participant
                reading/relying upon such views/comment/suggestion including
                view/comments/suggestion with respect to any financial product
                or class of financial products and financial market conditions.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The participant agrees and acknowledges that trading in
                Derivative instruments / investments in stocks or any other
                financial products are subject to market risks (including
                possible loss of the principal amount invested), and the
                participant shall read all investment/scheme-related documents
                carefully, and make its own assessment before making any
                investment/disinvestment decision.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You agree to defend, indemnify and hold the Group Admin, the
                Company and/or its group entities, its affiliates, directors,
                officers, employees, agents, partners and licensors harmless
                from any claim or demand, including reasonable attorney’s fees,
                made by a third party or any other participant in the Groups,
                relating to or arising from: (a) any content You submit, post,
                transmit, or otherwise make available in and through the Groups;
                (b) Your use of the Groups; and/ or (c) any violation by You of
                the Terms of Use. This obligation shall survive the termination
                or expiration of these Terms of Use or termination of your usage
                of the Groups by You and due to Your removal by the Group Admin.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                You agree and acknowledge that Your personal information
                including your mobile number disclosed in the Groups may be used
                by the participants and/or the Groups Admin/the Company to
                communicate or establish contact with you or for any other
                purposes. You hereby further unconditionally consents that such
                communications via SMS/text messaging services and/ or voice
                call by any other participant, Group Admin/ Company is (a) upon
                the request and/or authorization by you, (b) ‘transactional’ and
                not an ‘unsolicited commercial communication’ under applicable
                laws including under the guidelines issued by Telecom Regulation
                Authority of India (“TRAI”) and (c) in compliance with the
                relevant guidelines of TRAI or such other authority in India and
                abroad. You shall indemnify the Group Admin and/or Company
                against all types of losses and damages incurred by it or its
                affiliates ,group entities, directors, officers, employees,
                agents, partners and licensors due to any action taken by TRAI,
                access providers (as per TRAI regulations) or any other
                authority due to any erroneous compliant raised by you or anyone
                on the Group Admin and/or Company or its affiliates, group
                entiites, directors, officers, employees, agents, partners and
                licensors with respect to the intimations mentioned above or due
                to a wrong number or other contact details provided by you for
                any reason whatsoever.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Posts
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company may provide and display content on the Platform
                which features specific articles/write ups by third parties in
                relation to the financial instruments and securities markets.
                You hereby agree and acknowledge that such content displayed on
                the Platform does not represent the views and/ or
                recommendations of the Company and You are required to read the
                documents carefully before investing in any mutual funds and/ or
                stocks. You may also be allowed to post and comment on such
                content on the Platform and You hereby undertake to ensure that
                such comments shall not be offensive and will be in accordance
                with applicable laws. All material added, created, submitted, or
                posted to the Platform by You is Your sole responsibility. The
                Company reserves the right to review any information
                provided/data uploaded, if any, by You on the Platform and
                delete any information/data that is inconsistent with these
                Terms of Use.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                General Provisions
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                Notice: All notices from the Company will be served by email to
                Your registered email address or by general notification on the
                Platform. Any notice provided to the Company pursuant to the
                Terms of Use should be sent to{" "}
                <MuiLink href="mailto:foxtox.care@gmail.com">
                  foxtox.care@gmail.com
                </MuiLink>{" "}
                with subject line – Attention: TERMS OF USE.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Assignment: You cannot assign or otherwise transfer the Terms of
                Use, or any rights granted hereunder to any third Party. The
                Company’s rights under the Terms of Use are freely transferable
                by the Company to any third party without the requirement of
                seeking Your consent.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Severability: If, for any reason, a court of competent
                jurisdiction finds any provision of the Terms of Use, or any
                portion thereof, to be unenforceable, that provision shall be
                enforced to the maximum extent permissible so as to give effect
                to the intent of the parties as reflected by that provision, and
                the remainder of the Terms of Use shall continue in full force
                and effect.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Waiver: Any failure by the Company to enforce or exercise any
                provision of the Terms of Use, or any related right, shall not
                constitute a waiver by the Company of that provision or right.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                IP Infringement
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                If You believe the Platform violates Your intellectual property,
                You must promptly notify the Company in writing at{" "}
                <MuiLink href="mailto:foxtox.care@gmail.com">
                  foxtox.care@gmail.com
                </MuiLink>
                . These notifications should only be submitted by the owner of
                the intellectual property or an agent authorized to act on
                his/her behalf. However, any false claim by You may result in
                the termination of Your access to the Platform. You are required
                to provide the following details in Your notice:
              </Typography>
              <List sx={{ pl: 2 }}>
                {[
                  "(i) the intellectual property that You believe is being infringed;",
                  "(ii) the item that You think is infringing and include sufficient information about where the material is located on the Platform;",
                  "(iii) a statement that You believe in good faith that the item You have identified as infringing is not authorized by the intellectual property owner, its agent, or the law to be used in connection with the Platform;",
                  "(iv) Your contact details, such as Your address, telephone number, and/or email;",
                  "(v) a statement that the information You provided in Your notice is accurate, and that You are the intellectual property owner or an agent authorized to act on behalf of the intellectual property owner whose intellectual property is being infringed; and",
                  "(vi) Your physical or electronic signature.",
                ].map((item, index) => (
                  <CustomListItem key={index} disableGutters>
                    <ListItemText
                      primaryTypographyProps={{ lineHeight: "1.6" }}
                      primary={item}
                    />
                  </CustomListItem>
                ))}
              </List>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Force Majeure
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                The Company shall not be liable to You for its failure to
                perform or for delay in providing You access to Your account or
                any Platform Services thereof, to the extent such failure or
                delay results from causes beyond its reasonable control,
                including, without limitation, acts of God, fires, explosions,
                wars or other hostilities, sabotage, civil unrest, network
                disruptions or failures, change in laws, rules and regulations,
                insurrections, revolutions, strikes, labour unrest, earthquakes,
                floods, pandemic, epidemics or regulatory or quarantine
                restrictions, unforeseeable governmental restrictions or actions
                or controls or a failure by a third party hosting provider or
                death of The Advisor or internet service provider or on account
                of any change or defect in the software and/or hardware of Your
                computer system.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Your Content
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                In these Website/App Standard Terms and Conditions, "Your
                Content" shall mean any audio, video text, images or other
                material you choose to display on this Website/App. By
                displaying Your Content, you grant Investzen Ventures Private
                Limited a non-exclusive, worldwide irrevocable, sub-licensable
                license to use, reproduce, adapt, publish, translate and
                distribute it in any and all media.
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Your Content must be your own and must not be invading any
                third-party’s rights. Investzen Ventures Private Limited
                reserves the right to remove any of Your Content from this
                Website at any time without notice.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Your Privacy
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                Please read the{" "}
                <MuiLink href="/privacy-policy" color="primary" underline="hover">
                  Privacy Policy
                </MuiLink>
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Entire Agreement
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                These Terms constitute the entire agreement between Investzen
                Ventures Private Limited and you about your use of this
                Website/App, and supersede all prior agreements and
                understandings.
              </Typography>
              <Divider sx={{ my: 4 }} />
            </section>

            <section>
              <Typography
                variant="h5"
                component="h2"
                fontWeight="fontWeightBold"
                color="text.primary"
                sx={{ mb: 3 }}
              >
                Contact Us
              </Typography>
              <Typography variant="body1" paragraph lineHeight="1.6">
                If you have any questions about these Terms and Conditions, You
                can contact us through email:{" "}
                <MuiLink href="mailto:foxtox.care@gmail.com">
                  foxtox.care@gmail.com
                </MuiLink>
              </Typography>
            </section>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Terms;
